import {
  type Account,
  type InventoryResource,
  type ResourceType,
  type Snapshot,
  Provider,
} from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import type { RestoreGenericStorageProps } from './bucketSelection';
import { supportedCrossCloudRestores } from './restoreFileWizard';
import { S3Configuration } from './steps/s3Configuration';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreS3State extends RestoreGenericStorageProps {
  resourceType: ResourceType;
  resourceId: string;
  snapshot: Snapshot;
  supportedCloudProviders: Provider[];
  restoreAccount?: Account;
}

const restoreS3Flow: Step<RestoreS3State> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 's3_configuration',
      Component: S3Configuration,
    },
  ],
};

export const RestoreS3Wizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { vaults } = useBackupVaults();
  const { isDemo, isDev } = useEnvironment();

  const cloudProvider = vaults.find((v) => v.id === snapshot.vaultId)
    ?.vaultAttributes.cloudProvider;

  return (
    <Wizard<RestoreS3State>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreS3Flow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        resourceType: resource.resourceType,
        S3: {},
        GCS: {},
        StorageAccount: {},
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : ((cloudProvider && supportedCrossCloudRestores[cloudProvider]) ??
              []),
      }}
      title={
        resource.cloudProvider === Provider.Aws
          ? 'Restore S3'
          : resource.cloudProvider === Provider.Azure
            ? 'Restore Storage Account'
            : resource.cloudProvider === Provider.Gcp
              ? 'Restore GCS'
              : ''
      }
      stepperLabels={['Restore Account', 'Configuration']}
    />
  );
};
